// src/components/workloads/creation/TypeSelector.tsx
import React from 'react';
import { Pentagon, Upload, Grid3X3, MapPin, Check } from 'lucide-react';
import { useWorkloadStore } from '../../../store/workloadStore';

interface TypeOption {
  id: 'saturation' | 'csv_upload' | 'census_block' | 'draw_area';
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
}

const typeOptions: TypeOption[] = [
  {
    id: 'saturation',
    title: 'Saturation Point',
    description: 'Pick a point on the map and pull addresses outward',
    icon: <MapPin className="h-8 w-8" />,
    features: [
      'Click any point on the map',
      'Closest-first or walking-pattern modes',
      'Auto-discover addresses in the area',
      'Split into manageable workloads',
    ],
  },
  {
    id: 'draw_area',
    title: 'Draw Area',
    description: 'Draw a polygon anywhere on the map and pull addresses inside',
    icon: <Pentagon className="h-8 w-8" />,
    features: [
      'Draw one or more polygons',
      'Building-footprint reverse geocoding',
      'No center point required',
      'Download discovered addresses as CSV',
    ],
  },
  {
    id: 'csv_upload',
    title: 'CSV Upload',
    description: 'Upload a list of addresses from a spreadsheet',
    icon: <Upload className="h-8 w-8" />,
    features: [
      'Import from CSV or Excel',
      'Map columns to address fields',
      'Geocode addresses automatically',
      'Include custom data tags',
    ],
  },
  {
    id: 'census_block',
    title: 'Census Block',
    description: 'Select specific census blocks to target',
    icon: <Grid3X3 className="h-8 w-8" />,
    features: [
      'Browse by state and county',
      'View census block boundaries',
      'Target specific demographics',
      'Official census address data',
    ],
  },
];

const TypeSelector: React.FC = () => {
  const { creation, setCreationType } = useWorkloadStore();

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-gray-900">
          How would you like to create your workloads?
        </h2>
        <p className="text-gray-600 mt-2">
          Choose a method that best fits your canvassing needs
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {typeOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => setCreationType(option.id)}
            className={`relative text-left p-6 rounded-lg border-2 transition-all ${
              creation.type === option.id
                ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-200'
                : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
            }`}
          >
            {/* Selected indicator */}
            {creation.type === option.id && (
              <div className="absolute top-4 right-4 bg-indigo-600 text-white rounded-full p-1">
                <Check className="h-4 w-4" />
              </div>
            )}

            {/* Icon */}
            <div
              className={`inline-flex items-center justify-center w-14 h-14 rounded-lg mb-4 ${
                creation.type === option.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {option.icon}
            </div>

            {/* Title & Description */}
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {option.title}
            </h3>
            <p className="text-gray-600 text-sm mb-4">{option.description}</p>

            {/* Features */}
            <ul className="space-y-2">
              {option.features.map((feature, index) => (
                <li
                  key={index}
                  className="flex items-start text-sm text-gray-600"
                >
                  <Check
                    className={`h-4 w-4 mr-2 mt-0.5 flex-shrink-0 ${
                      creation.type === option.id
                        ? 'text-indigo-600'
                        : 'text-gray-400'
                    }`}
                  />
                  {feature}
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>

      {/* Tip */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> Saturation Point is the most common method for
          door-to-door canvassing. CSV Upload is best when you have a specific
          list of addresses to target.
        </p>
      </div>
    </div>
  );
};

export default TypeSelector;
